import type { W_Employee_FindDeactivatedList_VM } from 'ab-api/web/employee/findDeactivatedList';

import type { EmployeeListViewModel } from 'ab-viewModels/employee/list.viewModel';

const DEACTIVATED_CSV_REQUIRED_KEYS = [
	'ID',
	'fullName',
	'userType',
	'laborType',
	'wageClassification',
	'homeOffice',
	'SMSSubscribedStatus',
	'phoneNumber',
	'email',
];

const COMPANY_DEACTIVATED_CSV_REQUIRED_KEYS = [
	...DEACTIVATED_CSV_REQUIRED_KEYS,
	'showOnScheduleBoard',
	'skill.0.name',
	'skill.0.color',
];

const EMPTY_SKILL = {
	id: null,
	name: '',
	color: null,
};

export const deactivatedToCSVData = (viewModel: W_Employee_FindDeactivatedList_VM): string[][] => {
	let maxNumberOfSkills = 1;	// if skills are able to be shown on export, the first group of columns should always be visible
	const header: string[] = [...COMPANY_DEACTIVATED_CSV_REQUIRED_KEYS];

	viewModel.list.forEach((_item: W_Employee_FindDeactivatedList_VM['list'][number]) => {
		if (_item.skills.length > maxNumberOfSkills) {
			for (let _index = maxNumberOfSkills; _index < _item.skills.length; _index++) {
				header.push(`skill.${_index}.name`, `skill.${_index}.color`);
			}
			maxNumberOfSkills = _item.skills.length;
		}
	});

	const rows: string[][] = viewModel.list.map((_item: W_Employee_FindDeactivatedList_VM['list'][number]) => {
		const _row: string[] = [
			_item.uniqueId,
			_item.fullName,
			_item.accountTemplate,
			_item.wageRateType,
			_item.wageRateClassification,
			_item.locationNickname,
			_item.subscriptionStatus,
			_item.phoneNumber,
			_item.email,
		];
		_row.push(_item.showOnScheduleBoard ? 'Yes' : 'No');
		for (let _index = 0; _index < maxNumberOfSkills; _index++) {
			const _skill = _item.skills[_index] || EMPTY_SKILL;
			_row.push(_skill.name, _skill.color);
		}
		return _row;
	});

	return [header, ...rows];
};

const ACTIVE_CSV_REQUIRED_KEYS = [
	'ID',
	'fullName',
	'userType',
	'laborType',
	'wageClassification',
	'homeOffice',
	'SMSSubscribedStatus',
	'phoneNumber',
	'email',
];

const COMPANY_ACTIVE_CSV_REQUIRED_KEYS = [
	...ACTIVE_CSV_REQUIRED_KEYS,
	'showOnScheduleBoard',
	'skill.0.name',
	'skill.0.color',
];

const ORGANIZATION_ACTIVE_CSV_REQUIRED_KEYS = [
	...ACTIVE_CSV_REQUIRED_KEYS,
	'company',
];

export const activeToCSVData = (viewModel: EmployeeListViewModel, isOrganizationLevel: boolean = false): string[][] => {
	let maxNumberOfSkills = 1;	// if skills are able to be shown on export, the first group of columns should always be visible
	const header: string[] = isOrganizationLevel ? [...ORGANIZATION_ACTIVE_CSV_REQUIRED_KEYS] : [...COMPANY_ACTIVE_CSV_REQUIRED_KEYS];

	if (!isOrganizationLevel) {
		viewModel.list.forEach((_item: EmployeeListViewModel['list'][number]) => {
			if (_item.skills.length > maxNumberOfSkills) {
				for (let _index = maxNumberOfSkills; _index < _item.skills.length; _index++) {
					header.push(`skill.${_index}.name`, `skill.${_index}.color`);
				}
				maxNumberOfSkills = _item.skills.length;
			}
		});
	}

	const rows: string[][] = viewModel.list.map((_item: EmployeeListViewModel['list'][number]) => {
		const _row: string[] = [
			_item.uniqueId,
			_item.fullName,
			_item.accountTemplate,
			_item.wageRateType,
			_item.wageRateClassification,
			_item.locationNickname,
			_item.subscriptionStatus,
			_item.phoneNumber ?? '',
			_item.email,
		];
		if (isOrganizationLevel) {
			_row.push(_item.companyName);
		} else {
			_row.push(_item.showOnScheduleBoard ? 'Yes' : 'No');
			for (let _index = 0; _index < maxNumberOfSkills; _index++) {
				const _skill = _item.skills[_index] || EMPTY_SKILL;
				_row.push(_skill.name, _skill.color);
			}
		}
		return _row;
	});

	return [header, ...rows];
};
